/**
 * Dynamic Blog Post Page - WoodAlps Content Articles
 * 
 * This dynamic route page renders individual blog posts with rich content:
 * - Slug-based routing for SEO-friendly URLs
 * - Rich content rendering with custom PortableText components
 * - Responsive image and video embedding
 * - Social sharing and navigation features
 * - Author information and publication details
 * - Related posts and cross-linking
 * - SEO optimization with dynamic meta tags
 * 
 * Content Features:
 * - Full-width, body-width, and small image display options
 * - Video embedding with responsive containers
 * - Image captions and alt text support
 * - Custom typography and formatting
 * - Social media integration for sharing
 * 
 * Technical Features:
 * - Static generation with ISR for performance
 * - Dynamic slug-based content fetching
 * - Fallback handling for non-existent posts
 * - Image optimization with Next.js Image component
 * - SEO meta tags with Open Graph support
 * 
 * @description Dynamic blog post page with rich content rendering and SEO optimization
 */

import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { getBlogPost, getAllBlogPostSlugs } from '../../src/app/studio/lib/sanity';
import { urlForImage } from '../../src/app/studio/lib/image';
import { sanityLoader } from '../../utils/sanityLoader';
import Layout from '../../components/layout/Layout';
import PortableText from '../../components/content/PortableText';
import { useTranslation } from '../../src/hooks/useTranslation';

// Custom PortableText components for rich blog content rendering
const myPortableTextComponents = {
  types: {
    image: ({ value }) => {
      if (!value?.asset?._ref) {
        return null;
      }
      
      // Get display style
      const style = value.displayStyle || 'body-width';
      const widthClasses = {
        'full-width': 'w-full',
        'body-width': 'max-w-3xl mx-auto',
        'small': 'max-w-md mx-auto',
      };
      
      return (
        <div className={`my-8 relative ${widthClasses[style]}`}>
          <figure>
            <div className="relative w-full h-64 md:h-96">
              <Image
                src={urlForImage(value)}
                alt={value.alt || ''}
                fill
                loader={sanityLoader}
                sizes="(max-width: 768px) 100vw, 800px"
                className="rounded-lg object-cover"
              />
            </div>
            {value.caption && (
              <figcaption className="mt-2 text-sm text-center text-gray-500">
                {value.caption}
              </figcaption>
            )}
          </figure>
        </div>
      );
    },
    video: ({ value }) => {
      if (!value?.url) {
        return null;
      }
      
      // Define width classes based on size
      const sizeClasses = {
        small: 'max-w-xs',
        medium: 'max-w-2xl',
        large: 'max-w-4xl',
        full: 'w-full',
      };
      
      const size = value.size || 'medium';
      
      // Determine aspect ratio
      const aspectRatioClasses = {
        '16:9': 'aspect-video',
        '4:3': 'aspect-[4/3]',
        '1:1': 'aspect-square',
        '9:16': 'aspect-[9/16]',
      };
      
      const aspectRatio = value.aspectRatio || '16:9';
      
      // Process embed URL based on provider
      let embedUrl = value.url;
      if (value.videoProvider === 'youtube') {
        // Extract YouTube ID and format as embed URL
        const youtubeRegExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
        const match = value.url.match(youtubeRegExp);
        if (match && match[2].length === 11) {
          embedUrl = `https://www.youtube.com/embed/${match[2]}`;
        }
      } else if (value.videoProvider === 'vimeo') {
        // Extract Vimeo ID and format as embed URL
        const vimeoRegExp = /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)([0-9]+)/;
        const match = value.url.match(vimeoRegExp);
        if (match) {
          embedUrl = `https://player.vimeo.com/video/${match[1]}`;
        }
      }
      
      return (
        <div className={`my-8 relative ${sizeClasses[size]} mx-auto`}>
          <div className={`${aspectRatioClasses[aspectRatio]} w-full overflow-hidden rounded-lg`}>
            <iframe
              src={embedUrl}
              title="Video Player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
          {value.caption && (
            <figcaption className="mt-2 text-sm text-center text-gray-500">
              {value.caption}
            </figcaption>
          )}
        </div>
      );
    },
    imageGrid: ({ value }) => {
      if (!value?.images || !value.images.length) {
        return null;
      }
      
      const { layout = 'equal' } = value;
      
      // Define width classes based on layout
      const getWidthClasses = (index) => {
        if (layout === 'equal') return 'w-1/2';
        if (layout === 'leftLarger' && index === 0) return 'w-2/3';
        if (layout === 'leftLarger' && index === 1) return 'w-1/3';
        if (layout === 'rightLarger' && index === 0) return 'w-1/3';
        if (layout === 'rightLarger' && index === 1) return 'w-2/3';
        return 'w-1/2';
      };
      
      return (
        <div className="my-8">
          <div className="flex flex-wrap -mx-2">
            {value.images.map((image, index) => (
              <div key={index} className={`px-2 ${getWidthClasses(index)}`}>
                <div className="relative aspect-square">
                  <Image
                    src={urlForImage(image)}
                    alt={image.alt || ''}
                    fill
                    loader={sanityLoader}
                    className="rounded-lg object-cover"
                  />
                </div>
                {image.caption && (
                  <figcaption className="mt-1 text-sm text-center text-gray-500">
                    {image.caption}
                  </figcaption>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    },
    textAndImage: ({ value }) => {
      if (!value?.text || !value?.image?.asset?._ref) {
        return null;
      }
      
      const { layout = 'textLeft', imageWidth = 'medium' } = value;
      
      // Define classes for different image widths
      const imageWidthClasses = {
        small: layout === 'textLeft' ? 'md:w-3/10' : 'md:w-3/10',
        medium: layout === 'textLeft' ? 'md:w-1/2' : 'md:w-1/2',
        large: layout === 'textLeft' ? 'md:w-7/10' : 'md:w-7/10',
      };
      
      const textWidthClass = 
        imageWidth === 'small' ? 'md:w-7/10' : 
        imageWidth === 'large' ? 'md:w-3/10' : 
        'md:w-1/2';
      
      return (
        <div className="my-8">
          <div className={`flex flex-col ${layout === 'textLeft' ? 'md:flex-row' : 'md:flex-row-reverse'} gap-4 md:gap-8`}>
            <div className={`w-full ${textWidthClass}`}>
              <div className="prose prose-lg max-w-none">
                <PortableText value={value.text} />
              </div>
            </div>
            
            <div className={`w-full ${imageWidthClasses[imageWidth]}`}>
              <div className="relative aspect-square">
                <Image
                  src={urlForImage(value.image)}
                  alt={value.image.alt || ''}
                  fill
                  loader={sanityLoader}
                  sizes="(max-width: 768px) 100vw, 50vw"
                  className="rounded-lg object-cover"
                />
              </div>
              {value.image.caption && (
                <figcaption className="mt-2 text-sm text-center text-gray-500">
                  {value.image.caption}
                </figcaption>
              )}
            </div>
          </div>
        </div>
      );
    }
  },
  marks: {
    link: ({ children, value }) => {
      const rel = !value.href.startsWith('/') ? 'noreferrer noopener' : undefined;
      return (
        <a 
          href={value.href} 
          rel={rel} 
          className="text-accent-color hover:underline"
          target={!value.href.startsWith('/') ? '_blank' : undefined}
        >
          {children}
        </a>
      );
    }
  },
  block: {
    h1: ({ children }) => <h1 className="text-4xl font-bold mt-12 mb-4">{children}</h1>,
    h2: ({ children }) => <h2 className="text-3xl font-bold mt-10 mb-4">{children}</h2>,
    h3: ({ children }) => <h3 className="text-2xl font-bold mt-8 mb-4">{children}</h3>,
    h4: ({ children }) => <h4 className="text-xl font-bold mt-6 mb-4">{children}</h4>,
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-accent-color/30 pl-4 my-6 italic text-gray-700">{children}</blockquote>
    ),
    normal: ({ children }) => <p className="mb-6 text-base sm:text-lg text-gray-700 leading-relaxed">{children}</p>,
    alignLeft: ({ children }) => <p className="text-left mb-6 text-base sm:text-lg text-gray-700 leading-relaxed">{children}</p>,
    alignCenter: ({ children }) => <p className="text-center mb-6 text-base sm:text-lg text-gray-700 leading-relaxed">{children}</p>,
    alignRight: ({ children }) => <p className="text-right mb-6 text-base sm:text-lg text-gray-700 leading-relaxed">{children}</p>,
    alignJustify: ({ children }) => <p className="text-justify mb-6 text-base sm:text-lg text-gray-700 leading-relaxed">{children}</p>,
  },
  list: {
    bullet: ({ children }) => <ul className="list-disc pl-6 mb-6 space-y-2">{children}</ul>,
    number: ({ children }) => <ol className="list-decimal pl-6 mb-6 space-y-2">{children}</ol>,
  },
  listItem: {
    bullet: ({ children }) => <li className="text-base sm:text-lg text-gray-700">{children}</li>,
    number: ({ children }) => <li className="text-base sm:text-lg text-gray-700">{children}</li>,
  },
};

export default function BlogPost({ post }) {
  const router = useRouter();
  const { t, locale } = useTranslation();
  const currentLocale = locale || 'en';
  
  if (router.isFallback) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl">{t('pages.blog_loading')}</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl">{t('pages.blog_post_not_found')}</p>
      </div>
    );
  }

  const formattedDate = new Date(post.publishedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Get the appropriate alt text based on the current locale
  const getMainImageAlt = () => {
    if (post.mainImage) {
      const altKey = `alt${currentLocale.toUpperCase()}`;
      return post.mainImage[altKey] || post.mainImage.altEN || post.title || "Blog post image";
    }
    return "Blog post image";
  };

  return (
    <>
      <Head>
        <title>{`${post.title} | WoodAlps Blog`}</title>
        <meta name="description" content={post.excerpt} />
        {post.mainImage && (
          <meta property="og:image" content={urlForImage(post.mainImage)} />
        )}
        <style jsx global>{`
          pre {
            white-space: pre-wrap;
            word-wrap: break-word;
            overflow-wrap: break-word;
            max-width: 100%;
            overflow-x: auto;
          }
          
          code {
            white-space: pre-wrap;
            word-break: break-all;
          }
          
          .prose img {
            max-width: 100%;
            height: auto;
          }
          
          .prose a {
            word-break: break-all;
          }
        `}</style>
      </Head>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-6 py-8 sm:py-12 md:py-16">
        <div className="mb-6">
          <Link href="/blog" className="text-accent-color hover:text-accent-color/80 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
           
          </Link>
        </div>

        <article className="mx-auto">
          <header className="mb-8 sm:mb-10">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 mb-4 sm:mb-6 break-words">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center justify-between mb-4 sm:mb-6">
              <p className="text-sm sm:text-base font-semibold text-gray-900">
                {t('pages.blog_by')} {post.authorName || 'Unknown author'}
              </p>
              <time dateTime={post.publishedAt} className="text-xs sm:text-sm text-gray-500">
                {formattedDate}
              </time>
            </div>

            {post.mainImage && (
              <div className="relative w-full aspect-video mb-6 sm:mb-8">
                <Image
                  src={urlForImage(post.mainImage)}
                  alt={getMainImageAlt()}
                  fill
                  loader={sanityLoader}
                  sizes="100vw"
                  className="rounded-lg object-cover"
                  priority
                />
              </div>
            )}
          </header>

          <div className="prose prose-base sm:prose-lg prose-accent max-w-none md:max-w-6xl mx-auto break-words overflow-hidden">
            <div className="px-0 sm:px-2">
              <PortableText value={post.body} components={myPortableTextComponents} />
            </div>
          </div>
        </article>
      </main>
    </>
  );
}

// Use the Layout component for the page
BlogPost.getLayout = function getLayout(page) {
  return <Layout transparentHeader={false}>{page}</Layout>;
};

export async function getStaticPaths() {
  const slugs = await getAllBlogPostSlugs();
  
  console.log('Static paths being generated for blog slugs:', slugs);
  
  return {
    paths: slugs.map((slug) => ({
      params: { slug },
    })),
    fallback: true,
  };
}

export async function getStaticProps({ params, preview = false, locale = 'en' }) {
  try {
    // Query for the post with the given slug
    const post = await getBlogPost(params.slug, preview, locale);
    
    if (!post) {
      console.error(`No post found for slug: "${params.slug}"`);
      return {
        notFound: true,
      };
    }
    
    return {
      props: {
        post,
      },
      // Regenerate on every request in preview, otherwise once per day
      revalidate: process.env.VERCEL_ENV === 'preview' ? 1 : 86400,
    };
  } catch (error) {
    console.error(`Error in getStaticProps for slug "${params.slug}":`, error);
    return {
      notFound: true,
    };
  }
} 
